export const RAW_MANUAL_SECTIONS = [
  {
    id: "platform-overview",
    title: "Visão geral da plataforma",
    category: "Início",
    audience: "Todos",
    journeyStage: "Início rápido",
    summary:
      "Entenda como a plataforma está organizada, com dashboard, menu lateral em abas e atalhos para iniciar atividades rapidamente.",
    whenToUse: [
      "Apresentar o funcionamento da solução para novos usuários ou equipes em treinamento.",
      "Relembrar onde ficam os módulos principais antes de executar demandas específicas.",
    ],
    steps: [
      "Após o login, revise o painel inicial com cartões de tarefas, lembretes vindos da Agenda e atalhos para abrir solicitações ou relatórios.",
      "Utilize o menu lateral — expansível ou compacto — para acessar módulos de Operações, Automação, Administração e Inteligência.",
      "Abra os itens desejados; cada módulo é carregado em uma aba interna com botão × para facilitar a alternância entre atividades.",
      "Use a busca do menu e o atalho “Nova solicitação” para chegar rapidamente ao processo ou formulário que precisa executar.",
    ],
    tips: [
      "Recolha o menu lateral pelo botão de seta quando precisar de mais espaço para formulários extensos ou relatórios.",
      "Feche abas concluídas para manter a navegação fluida e evitar trabalhar com dados desatualizados em sessões antigas.",
    ],
    related: ["Painel inicial e navegação", "Painel analítico do dashboard", "Assistente IA do Studio"],
  },
  {
    id: "request-lifecycle",
    title: "Ciclo completo de uma solicitação",
    category: "Início",
    audience: "Todos",
    journeyStage: "Início rápido",
    summary:
      "Veja como abrir, acompanhar, colaborar e finalizar solicitações utilizando os módulos de Solicitações e o Process Runner.",
    whenToUse: [
      "Mapear o fluxo padrão antes de treinar equipes ou revisar SLAs de atendimento.",
      "Entender onde localizar históricos, anexos e comprovantes depois da conclusão do processo.",
    ],
    steps: [
      "No Dashboard ou em Solicitações, clique em “Nova solicitação” e escolha o processo publicado que deseja iniciar.",
      "Preencha o formulário da etapa inicial e envie para liberar a fase seguinte; use comentários ou eventos para registrar observações relevantes.",
      "Monitore prazos pela Caixa de solicitações aplicando filtros por status, responsável ou processo para assumir tarefas pendentes.",
      "Quando houver necessidade de intervenção, abra o Process Runner para reatribuir responsáveis, reabrir etapas ou avançar manualmente conforme permissões.",
      "Após a conclusão, utilize a aba Concluídas para exportar relatórios, baixar PDFs e revisar toda a linha do tempo.",
    ],
    tips: [
      "Registre eventos sempre que ajustar dados manualmente para manter a trilha de auditoria completa.",
      "Combine filtros por período e processo com a exportação em Excel para produzir relatórios de acompanhamento ou compliance.",
    ],
    related: ["Caixa de solicitações", "Detalhamento e colaboração na solicitação", "Execução guiada de processos"],
  },
  {
    id: "login-access",
    title: "Acesso ao sistema",
    category: "Início",
    audience: "Todos",
    journeyStage: "Início rápido",
    summary:
      "Entre com seu e-mail corporativo, controle o lembrete local de credenciais e saiba interpretar retornos de erro.",
    whenToUse: [
      "Acessar a plataforma pela primeira vez ou depois de sair da conta.",
      "Habilitar ou remover o lembrete de credenciais em um computador confiável.",
    ],
    steps: [
      "Abra a URL da plataforma e confirme que o cabeçalho “Área de Acesso” está visível.",
      "Informe seu e-mail corporativo e a senha definida pelo administrador.",
      "Ative a opção “Lembrar usuário e senha” somente em dispositivos seguros; os dados ficam salvos no armazenamento local do navegador.",
      "Clique em “Entrar” e aguarde o redirecionamento para o dashboard. Mensagens de erro aparecem acima do formulário caso as credenciais não sejam aceitas.",
    ],
    tips: [
      "Para apagar um lembrete já salvo, desmarque a opção e realize um novo login; o sistema remove os dados armazenados.",
      "Caso esqueça a senha, solicite a um administrador a redefinição em Configurações → Usuários.",
    ],
    related: ["Perfil"],
  },
  {
    id: "home-dashboard",
    title: "Painel inicial e navegação",
    category: "Início",
    audience: "Todos",
    journeyStage: "Início rápido",
    summary:
      "Conheça o painel inicial, acompanhe indicadores operacionais, acesse notificações e organize módulos em abas durante a sessão.",
    whenToUse: [
      "Mapear rapidamente o status das solicitações e processos ao iniciar o dia.",
      "Abrir diferentes módulos em paralelo sem perder o contexto de trabalho.",
    ],
    steps: [
      "Revise os cartões de destaque para verificar solicitações abertas, pendências da equipe e SLAs próximos do vencimento.",
      "Utilize o menu lateral para alternar entre modo compacto e expandido, pesquisar itens pelo nome e acionar o atalho “Nova solicitação”.",
      "Abra o sino de notificações para conferir solicitações recém-atribuídas; ao visualizar um item ele é marcado automaticamente como lido.",
      "Clique em qualquer item do menu para carregar o módulo em uma nova aba interna. Feche abas com o botão × quando concluir a atividade.",
    ],
    tips: [
      "No celular, use o botão de menu para exibir a navegação completa sem sair da tela atual.",
      "Filtros e buscas aplicados em uma aba permanecem válidos enquanto ela estiver aberta, facilitando comparações lado a lado.",
    ],
    related: ["Perfil", "Solicitações", "Agenda"],
  },
  {
    id: "profile-preferences",
    title: "Perfil e preferências",
    category: "Início",
    audience: "Todos",
    journeyStage: "Início rápido",
    summary:
      "Atualize dados pessoais, senha e preferências de notificações diretamente do módulo Perfil.",
    whenToUse: [
      "Manter informações de contato corretas para a equipe e para os relatórios.",
      "Ativar ou desativar alertas por e-mail enviados pela plataforma.",
    ],
    steps: [
      "Abra Configurações → Perfil (ou selecione “Perfil” no menu lateral) para carregar seus dados atuais.",
      "Atualize nome para exibição, e-mail e data de nascimento; clique em “Salvar alterações” ao final do formulário.",
      "Use a caixa de seleção “Receber notificações por e-mail” para controlar lembretes adicionais sobre tarefas atribuídas.",
      "Informe uma nova senha sempre que solicitado pelo sistema e confirme o envio para encerrar a exigência de troca.",
    ],
    tips: [
      "Os campos de setor e grupo refletem cadastros definidos pelos administradores e podem ser consultados aqui antes de abrir chamados.",
    ],
    related: ["Usuários", "Segurança"],
  },
  {
    id: "requests-inbox",
    title: "Caixa de solicitações",
    category: "Operações",
    audience: "Colaboradores",
    journeyStage: "Execução diária",
    summary:
      "Monitore solicitações em andamento com filtros rápidos, indicadores de SLA e abertura direta das tarefas em abas dedicadas.",
    whenToUse: [
      "Executar demandas que dependem da sua aprovação ou atuação diária.",
      "Repriorizar solicitações pendentes com base em prazo, processo ou responsável.",
    ],
    steps: [
      "Acesse o módulo Solicitações para visualizar os cartões de volume (abertas, pendentes da equipe, em atraso e concluídas).",
      "Utilize o seletor de Usuário e os botões de atalho (“Solicitações do usuário” ou “Resolvidas pelo usuário”) para focar em um colaborador específico quando necessário.",
      "Refine a lista com a busca por texto, filtros por processo e prazo e, se necessário, abra os Filtros avançados para definir solicitante, responsável e período.",
      "Use os botões superiores para Nova solicitação, Atualizar e Exportar planilhas. Clique em uma linha ou no botão “Abrir solicitação” para tratar a tarefa na aba do processo.",
    ],
    tips: [
      "O botão “Limpar filtros” zera rapidamente busca, prazos e filtros avançados quando você precisar iniciar uma nova consulta.",
      "Os relatórios “Exportar por processo” e “Relatório completo” geram arquivos prontos para auditoria e compartilhamento com outras áreas.",
    ],
    related: ["Solicitações concluídas", "Processos", "Agenda"],
  },
  {
    id: "requests-completed",
    title: "Solicitações concluídas",
    category: "Operações",
    audience: "Colaboradores",
    journeyStage: "Execução diária",
    summary:
      "Consulte histórico completo, formulários e anexos de solicitações finalizadas e gere relatórios personalizados.",
    whenToUse: [
      "Resgatar decisões para auditorias internas ou externas.",
      "Conferir produtividade por processo, período ou responsável.",
    ],
    steps: [
      "No módulo Solicitações, selecione o filtro “Concluídas” para exibir apenas execuções finalizadas.",
      "Defina período, processo e participantes pelos filtros rápidos ou pelo painel de Filtros avançados.",
      "Use o botão “Histórico” em cada linha para expandir a linha do tempo das etapas ou abra a visão detalhada para baixar o PDF da solicitação.",
      "Gere planilhas com “Exportar Excel”, “Exportar por processo” ou “Relatório completo” respeitando os filtros aplicados.",
    ],
    tips: [
      "A visão detalhada mantém os formulários preenchidos agrupados por seção, facilitando conferências ponto a ponto.",
      "Combine exportações filtradas com os registros da Auditoria do sistema para mapear quem executou cada ação.",
    ],
    related: ["Auditoria", "Solicitações", "Processos"],
  },
  {
    id: "request-detail",
    title: "Detalhamento e colaboração na solicitação",
    category: "Operações",
    audience: "Colaboradores",
    journeyStage: "Execução diária",
    summary:
      "Execute a etapa atual preenchendo o formulário, anexando arquivos, registrando eventos e enviando a tarefa para o próximo responsável.",
    whenToUse: [
      "Complementar dados ou anexos solicitados na etapa em execução.",
      "Assumir uma tarefa pendente, registrar um evento manual ou finalizar o fluxo.",
    ],
    steps: [
      "Abra a solicitação na aba dedicada e revise o cabeçalho com processo, solicitante, prazo e status.",
      "Clique em “Assumir tarefa” quando disponível para liberar o envio do formulário ou utilize “Liberar” caso precise devolver a responsabilidade.",
      "Preencha os campos organizados por grupos, anexando arquivos nos campos do tipo upload; o sistema valida obrigatórios e mantém rascunhos locais por etapa.",
      "Se houver eventos pendentes, use “Registrar evento” antes de enviar. Finalize com o botão “Enviar” para avançar o fluxo ou concluir o processo.",
      "Utilize o botão “Baixar PDF” para gerar um resumo assinado da solicitação quando necessário.",
    ],
    tips: [
      "Os rascunhos ficam salvos no navegador até que a etapa seja enviada, permitindo retomar o preenchimento depois.",
      "Campos bloqueados indicam dados herdados de etapas anteriores e ajudam a manter a rastreabilidade das informações.",
    ],
    related: ["Solicitações", "Arquivos", "Automação"],
  },
  {
    id: "new-request",
    title: "Iniciar nova solicitação",
    category: "Operações",
    audience: "Colaboradores",
    journeyStage: "Execução diária",
    summary:
      "Selecione processos publicados no catálogo, inicie solicitações imediatas ou agende execuções recorrentes.",
    whenToUse: [
      "Abrir demandas operacionais como compras, viagens ou cadastros padronizados.",
      "Programar solicitações automáticas para eventos futuros utilizando agendas.",
    ],
    steps: [
      "No módulo Solicitações, clique em “Nova solicitação” para abrir o catálogo organizado por categorias.",
      "Pesquise pelo nome ou descrição, expanda a categoria desejada e avalie o cartão com número do processo e instruções.",
      "Use “Iniciar agora” para abrir imediatamente o formulário na aba do processo ou selecione “Agendar” para configurar recorrência via Schedule Configurator.",
      "Confirme o agendamento com “Confirmar agendamento” ou acompanhe a execução recém-aberta pela aba Solicitações.",
    ],
    tips: [
      "O botão “Atualizar lista” recarrega processos publicados recentemente sem precisar fechar a aba.",
      "Categorias e etiquetas auxiliam na padronização: combine-as com a busca para encontrar fluxos específicos rapidamente.",
    ],
    related: ["Processos", "Formulários", "Agenda"],
  },
  {
    id: "processes-catalog",
    title: "Catálogo de processos",
    category: "Operações",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Gerencie o catálogo de processos, aplique filtros por status, utilize a IA para analisar fluxos e acione integrações ou exportações.",
    whenToUse: [
      "Controlar versões publicadas e rascunhos antes de liberar um fluxo para a operação.",
      "Identificar processos com maior adoção ou necessidade de integração externa.",
    ],
    steps: [
      "Acesse Processos para visualizar os indicadores de volume total, publicados e rascunhos.",
      "Use os filtros de status, categoria, período e busca textual para encontrar rapidamente o fluxo desejado.",
      "Acione os botões de ação de cada cartão para editar, exportar pacotes, abrir as configurações de integração ou analisar o processo com o Assistente IA.",
      "Crie novos processos pelo botão “Novo processo”, importe pacotes existentes ou gere fluxos sugeridos com o botão “Assistente IA”.",
    ],
    tips: [
      "O selo de status no canto superior direito do cartão indica se o fluxo está publicado ou em rascunho.",
      "Use “Exportar por processo” para gerar backups individuais antes de alterações estruturais importantes.",
    ],
    related: ["Modelagem", "Solicitações", "Automação"],
  },
  {
    id: "process-modeler",
    title: "Modelagem de processos",
    category: "Automação",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Modele processos em BPMN, reutilize templates, configure propriedades de tarefas e publique versões prontas para execução.",
    whenToUse: [
      "Criar um fluxo do zero ou ajustar etapas, eventos e gateways existentes.",
      "Gerar documentação em PDF ou salvar o desenho como modelo reutilizável.",
    ],
    steps: [
      "Clique em “Editar” em um processo para abrir o Modelador e escolha um template pré-definido ou um fluxo em branco.",
      "Arraste tarefas, eventos e gateways para o canvas, conectando-os pelas setas de sequência conforme o desenho desejado.",
      "Selecione cada elemento para definir responsáveis, prazos, formulários associados e regras de transição no painel lateral.",
      "Use os botões superiores para validar o diagrama, salvar como rascunho, publicar ou gerar o PDF do processo para documentação.",
    ],
    tips: [
      "Salve variações frequentes como modelos personalizados para acelerar a criação de novos fluxos.",
      "O botão “Imprimir PDF” gera um resumo visual do processo, útil para auditorias e treinamentos presenciais.",
    ],
    related: ["Processos", "Formulários", "Automação"],
  },
  {
    id: "process-runner",
    title: "Execução guiada de processos",
    category: "Operações",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Acompanhe execuções em tempo real, avance etapas manualmente quando necessário e visualize indicadores de progresso.",
    whenToUse: [
      "Apoiar equipes com dificuldades em etapas específicas.",
      "Reprocessar etapas manuais após correções de dados ou anexos.",
    ],
    steps: [
      "Abra Processos → “Execução” para buscar a instância desejada pelo ID ou solicitante.",
      "Verifique a linha do tempo das etapas, com status e responsáveis destacados.",
      "Quando autorizado, utilize “Avançar manualmente” ou “Reabrir etapa” para ajustar o fluxo.",
      "Registre observações para documentar a intervenção e manter o histórico completo.",
    ],
    tips: [
      "Intervenções manuais ficam registradas na auditoria; utilize-as com critério.",
      "Ative alertas de SLA para ser notificado antes de uma etapa vencer.",
    ],
    related: ["Solicitações", "Auditoria", "Automação"],
  },
  {
    id: "forms-catalog",
    title: "Catálogo de formulários",
    category: "Operações",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Gerencie o catálogo de formulários reutilizáveis, gere sugestões com IA, controle links externos e mantenha versões sempre atualizadas.",
    whenToUse: [
      "Organizar formulários por departamento ou tipo de solicitação.",
      "Compartilhar formulários via link externo ou reutilizá-los em múltiplos processos.",
    ],
    steps: [
      "Abra Formulários para conferir os cartões com nome, descrição, data de criação e indicação de link externo ativo.",
      "Utilize a busca, o filtro de descrição e o período para localizar rapidamente o formulário desejado.",
      "Clique em “Novo Formulário” para abrir o construtor ou em “Assistente IA” para gerar uma sugestão com base em um prompt.",
      "Copie o link externo direto do cartão quando houver publicação pública e use os botões de ação para editar, duplicar ou remover o formulário.",
    ],
    tips: [
      "O botão “Copiar link” disponibiliza rapidamente o endereço público para incorporação em portais externos.",
      "Faça uma prévia antes de publicar alterações para validar regras condicionais e campos obrigatórios.",
    ],
    related: ["Form Builder", "Processos", "Formulários externos"],
  },
  {
    id: "form-builder",
    title: "Construtor de formulários",
    category: "Automação",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Construa formulários dinâmicos arrastando campos, configurando validações, regras condicionais e pré-visualizando o resultado em tempo real.",
    whenToUse: [
      "Criar formulários reutilizáveis ou específicos para um processo.",
      "Aplicar máscaras, listas dinâmicas e lógica condicional sem escrever código.",
    ],
    steps: [
      "Abra um formulário no modo de edição para acessar o construtor com painel de componentes à esquerda.",
      "Arraste campos de texto, números, anexos, seleções dinâmicas ou grupos para o layout e ajuste o tamanho de cada coluna.",
      "Defina rótulos, obrigatoriedade, máscaras, descrições e expressões condicionais na área de propriedades do campo.",
      "Utilize os botões superiores para salvar, salvar como rascunho ou abrir a prévia em uma nova aba antes de publicar.",
    ],
    tips: [
      "Organize campos em grupos para controlar modos de edição e leitura por etapa do processo.",
      "Dê nomes únicos e padronizados aos campos para facilitar integrações e geração de relatórios.",
    ],
    related: ["Formulários", "Automação", "Processos"],
  },
  {
    id: "form-preview-external",
    title: "Formulários externos e pré-visualização",
    category: "Operações",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Disponibilize formulários para públicos externos com tokens seguros e valide a experiência em uma pré-visualização interativa.",
    whenToUse: [
      "Abrir canais de atendimento para clientes, parceiros ou fornecedores sem acesso à plataforma.",
      "Testar o layout e as validações do formulário antes de publicá-lo.",
    ],
    steps: [
      "Ative o link externo do formulário e copie o endereço diretamente do cartão no catálogo.",
      "Compartilhe o link público ou incorpore-o em portais internos utilizando o endereço iFrame exibido quando habilitado.",
      "Acesse “Prévia” no construtor para simular o preenchimento, validar regras condicionais e garantir responsividade.",
      "Quando necessário, gere um novo token para revogar acessos anteriores ou limitar o número de respostas aceitas.",
    ],
    tips: [
      "Revise periodicamente quem está divulgando o link externo para garantir que somente a versão vigente seja utilizada.",
      "Use a pré-visualização para validar campos de upload e máscaras em dispositivos móveis antes da publicação.",
    ],
    related: ["Formulários", "Segurança", "Processos"],
  },
  {
    id: "automation-hub",
    title: "Hub de automação",
    category: "Automação",
    audience: "TI",
    journeyStage: "Execução diária",
    summary:
      "Centralize métricas de integrações, filtre processos conectados e abra rapidamente a configuração de webhooks e robôs.",
    whenToUse: [
      "Avaliar adoção de automações por processo e identificar oportunidades de integração.",
      "Criar novos conectores ou revisar integrações ativas sem navegar processo por processo.",
    ],
    steps: [
      "Acesse Automação → Hub para visualizar indicadores de processos com integrações ativas, em rascunho ou pendentes.",
      "Utilize a busca e o filtro de status (com ou sem automação) para localizar o processo desejado.",
      "Clique no cartão de cada processo para abrir as configurações detalhadas de integração em uma nova aba.",
      "Aproveite os templates prontos (RPA de preços, Webhook, UiPath, Power Automate) para iniciar integrações com parâmetros sugeridos.",
    ],
    tips: [
      "Os percentuais exibidos no topo ajudam a priorizar quais processos ainda não possuem automação configurada.",
      "Abra o mesmo processo diretamente do Hub sempre que precisar ajustar chaves ou reprocessar eventos sem passar pelo catálogo de processos.",
    ],
    related: ["Integrações", "Processos", "Auditoria"],
  },
  {
    id: "integration-settings",
    title: "Configurações de integrações",
    category: "Automação",
    audience: "TI",
    journeyStage: "Execução diária",
    summary:
      "Configure webhooks e integrações por processo, ajuste cabeçalhos e eventos monitorados e exporte dados de solicitações para sistemas externos.",
    whenToUse: [
      "Preparar um processo para enviar eventos ao CRM, ERP ou plataforma RPA.",
      "Atualizar credenciais, URLs ou status de integrações existentes.",
    ],
    steps: [
      "Abra as Configurações de integração a partir do processo ou do Hub de Automação para listar webhooks cadastrados.",
      "Clique em “Nova integração” para informar nome, URL, método HTTP, tempo limite e eventos que devem disparar a requisição.",
      "Adicione cabeçalhos personalizados, ajuste o status ativo/inativo e salve a configuração para que o processo passe a enviar os dados.",
      "Utilize a seção “Exportação de dados” para gerar arquivos CSV com solicitações concluídas ou em andamento filtradas por período e status.",
    ],
    tips: [
      "Guarde descrições claras sobre o sistema de destino em cada integração para facilitar auditorias futuras.",
      "Ao rotacionar credenciais, marque a opção de limpar senha/autenticação para garantir que nenhum dado antigo permaneça armazenado.",
    ],
    related: ["Automação", "Processos", "TI"],
  },
  {
    id: "agenda-collaboration",
    title: "Agenda colaborativa",
    category: "Operações",
    audience: "Colaboradores",
    journeyStage: "Execução diária",
    summary:
      "Planeje atividades pessoais e de equipe, envie lembretes para usuários ou setores e sincronize calendários externos via iCal.",
    whenToUse: [
      "Controlar prazos de aprovação, reuniões operacionais e eventos recorrentes ligados aos processos.",
      "Distribuir lembretes automáticos para toda a equipe diretamente da plataforma.",
    ],
    steps: [
      "Acesse a Agenda para visualizar o formulário de criação à esquerda e os painéis de eventos, aniversários e integrações à direita.",
      "Cadastre um novo evento informando título, data, observações e, se possuir permissão, selecione usuários, setores ou toda a base para envio automático.",
      "Use os botões de preferências para controlar se os próximos eventos aparecem no dashboard e ajuste lembretes individuais quando necessário.",
      "Conecte agendas externas preenchendo o link iCal/ICS e utilize os botões de sincronizar, atualizar ou remover integrações conforme o provedor.",
    ],
    tips: [
      "Ativar a opção “Enviar para todos” dispara o lembrete individualmente para cada usuário cadastrado.",
      "Revise periodicamente as integrações conectadas e utilize o botão de sincronização manual para garantir que novos eventos sejam importados imediatamente.",
    ],
    related: ["Solicitações", "Processos", "Notificações"],
  },
  {
    id: "file-manager",
    title: "Gestão de arquivos",
    category: "Operações",
    audience: "Colaboradores",
    journeyStage: "Execução diária",
    summary:
      "Organize anexos provenientes dos processos, filtre por categoria, gerencie permissões e baixe arquivos diretamente do repositório central.",
    whenToUse: [
      "Reunir documentos usados em formulários ou etapas específicas de processos.",
      "Controlar quem pode visualizar ou baixar evidências de auditoria.",
    ],
    steps: [
      "Acesse Arquivos para navegar pela hierarquia categoria → formulário → campo utilizando os filtros de busca e categoria.",
      "Selecione uma pasta para abrir o visualizador, aplicar filtros por ano/mês, pesquisar pelo nome do arquivo e baixar evidências.",
      "Se possuir permissão, ajuste quem pode acessar cada pasta clicando em “Gerenciar acesso” e selecionando usuários ou setores autorizados.",
      "Use os botões de ação para baixar, excluir arquivos obsoletos ou remover categorias inteiras quando não forem mais necessárias.",
    ],
    tips: [
      "A pesquisa considera nomes de campos, formulários e categorias: combine termos para localizar anexos rapidamente.",
      "Verifique o resumo da pasta para identificar rapidamente o volume de arquivos por mês e ajustar filtros antes do download.",
    ],
    related: ["Solicitações", "Lixeira", "Auditoria"],
  },
  {
    id: "my-team",
    title: "Minha equipe",
    category: "Operações",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Visualize o desempenho da equipe, assuma ou libere tarefas do time e registre lembretes compartilhados.",
    whenToUse: [
      "Identificar gargalos de solicitações sob responsabilidade da equipe.",
      "Criar lembretes coletivos para redistribuir prazos ou reuniões.",
    ],
    steps: [
      "Acesse Configurações → Minha equipe para conferir os indicadores de solicitações em andamento, concluídas e atrasadas.",
      "Filtre por colaborador para visualizar apenas as tarefas atribuídas a um membro específico e use os botões “Assumir” ou “Liberar” conforme necessário.",
      "Abra o modal de evento para registrar compromissos compartilhados com o time, definindo título, data e notificação no dashboard.",
      "Consulte a lista de integrantes para verificar grupo, setor e quantidade de tarefas em curso antes de redistribuir demandas.",
    ],
    tips: [
      "Utilize o filtro “Somente atrasos” (quando disponível) para atacar rapidamente os casos críticos da equipe.",
      "Anote decisões importantes no campo de descrição do evento da equipe para manter todos alinhados ao mesmo tempo.",
    ],
    related: ["Organograma", "Solicitações", "Agenda"],
  },
  {
    id: "organization-chart",
    title: "Organograma",
    category: "Operações",
    audience: "Todos",
    journeyStage: "Execução diária",
    summary:
      "Explore a hierarquia corporativa, pesquise colaboradores e visualize a quantidade de subordinados diretamente no organograma interativo.",
    whenToUse: [
      "Encontrar responsáveis antes de atribuir solicitações ou abrir chamados.",
      "Apresentar a estrutura da organização para novos colaboradores.",
    ],
    steps: [
      "Abra Configurações → Organograma para carregar o mapa hierárquico com zoom e rolagem horizontal.",
      "Utilize a busca para destacar colaboradores pelo nome, e-mail, grupo ou setor.",
      "Expanda ou recolha as ramificações utilizando as setas nos cartões para visualizar subordinados e contagens de equipe.",
      "Selecione um colaborador para consultar dados de contato, setor, grupo e relações diretas no painel lateral.",
    ],
    tips: [
      "A contagem exibida no cartão ajuda a identificar rapidamente lideranças com grandes equipes.",
      "Combine a busca com o filtro de colapso para focar apenas na unidade organizacional relevante.",
    ],
    related: ["Minha equipe", "Usuários", "Processos"],
  },
  {
    id: "audit-compliance",
    title: "Auditoria do sistema",
    category: "Administração",
    audience: "Administradores",
    journeyStage: "Administração",
    summary:
      "Pesquise registros de auditoria por usuário, ação e período, visualize metadados e acompanhe paginação em tempo real.",
    whenToUse: [
      "Rastrear quem realizou determinada ação em um processo ou configuração.",
      "Responder solicitações de compliance que exigem histórico de acessos.",
    ],
    steps: [
      "Acesse Configurações → Auditoria e informe filtros de data, usuário (ID), tipo de ação e termos de busca antes de aplicar.",
      "Clique em “Aplicar filtros” para carregar os eventos na tabela paginada e navegue entre páginas usando os controles inferiores.",
      "Abra “Ver detalhes” na coluna de Metadados para inspecionar a carga completa enviada/recebida, incluindo duração da requisição.",
    ],
    tips: [
      "O indicador de total encontrado ao lado do título ajuda a validar se a consulta retornou o volume esperado.",
      "Sempre limpe os filtros ao final de uma investigação para evitar conclusões com dados desatualizados em consultas futuras.",
    ],
    related: ["Usuários", "Processos", "Lixeira"],
  },
  {
    id: "trash-recovery",
    title: "Lixeira e restauração",
    category: "Administração",
    audience: "Administradores",
    journeyStage: "Administração",
    summary:
      "Restaure ou remova definitivamente processos, formulários, solicitações e arquivos dentro do período de retenção.",
    whenToUse: [
      "Reverter exclusões acidentais.",
      "Aplicar políticas de retenção, eliminando itens sensíveis em definitivo.",
    ],
    steps: [
      "Em Configurações → Lixeira, escolha o tipo de item (processo, formulário, solicitação, arquivo ou categoria) para refinar a lista.",
      "Analise a tabela de itens excluídos verificando quem realizou a remoção, quando ocorreu e quanto tempo resta antes da exclusão definitiva.",
      "Use os botões “Restaurar” ou “Excluir definitivamente” conforme a ação desejada; o sistema solicitará confirmação em ambos os casos.",
      "Clique em “Atualizar” sempre que precisar recarregar a lista após recuperar ou remover itens.",
    ],
    tips: [
      "O contador de expiração na coluna correspondente ajuda a priorizar quais itens precisam ser restaurados primeiro.",
      "Itens restaurados reaparecem no módulo de origem com as mesmas permissões e relacionamentos configurados anteriormente.",
    ],
    related: ["Arquivos", "Processos", "Auditoria"],
  },
  {
    id: "company-profile",
    title: "Dados da empresa",
    category: "Administração",
    audience: "Administradores",
    journeyStage: "Administração",
    summary:
      "Centralize dados institucionais, endereço, contatos e identidade visual exibidos nos formulários e comunicações.",
    whenToUse: [
      "Atualizar nome fantasia, e-mail oficial ou logotipo utilizado na plataforma.",
      "Registrar observações institucionais para consultas internas.",
    ],
    steps: [
      "Acesse Configurações → “Empresa” para visualizar a ficha atual e os campos editáveis.",
      "Envie uma nova logo pelo botão “Enviar logo” e utilize “Remover logo” se preferir voltar para o estado padrão.",
      "Preencha informações de contato, endereço e notas e clique em “Salvar” para publicar as alterações.",
    ],
    tips: [
      "Arquivos PNG ou SVG com fundo transparente rendem melhor no painel inicial e em formulários externos.",
      "Os campos são normalizados automaticamente para evitar espaços em branco desnecessários.",
    ],
    related: ["Formulários", "Processos", "Usuários"],
  },
  {
    id: "groups-management",
    title: "Administração de grupos",
    category: "Administração",
    audience: "Administradores",
    journeyStage: "Administração",
    summary:
      "Crie e mantenha grupos de acesso, revise permissões atribuídas e acompanhe indicadores de governança em tempo real.",
    whenToUse: [
      "Cadastrar novos perfis de acesso para times ou projetos.",
      "Auditar permissões concedidas a um grupo específico.",
    ],
    steps: [
      "Acesse Configurações → Grupos para conferir os cartões com totais de grupos e permissões cadastradas.",
      "Utilize os campos de busca e filtro por permissão para localizar rapidamente o grupo que precisa de ajustes.",
      "Clique em “Novo grupo” para definir nome e selecionar permissões; edite grupos existentes pelo mesmo modal quando precisar acrescentar ou remover direitos.",
      "Exclua grupos obsoletos apenas após garantir que nenhum usuário depende daquele perfil de acesso.",
    ],
    tips: [
      "Ordene as permissões pelo campo de busca dentro do modal para evitar a criação de grupos redundantes.",
      "Consulte os destaques acima da tabela para identificar rapidamente qual grupo concentra mais permissões sensíveis.",
    ],
    related: ["Usuários", "Processos", "Auditoria"],
  },
  {
    id: "users-management",
    title: "Gestão de usuários",
    category: "Administração",
    audience: "Administradores",
    journeyStage: "Administração",
    summary:
      "Cadastre usuários, ajuste grupos e setores, solicite trocas de senha e mantenha o diretório atualizado.",
    whenToUse: [
      "Realizar onboarding ou alterações cadastrais em massa.",
      "Aplicar políticas de segurança exigindo nova senha para perfis sensíveis.",
    ],
    steps: [
      "Acesse Configurações → Usuários para visualizar os indicadores de total por grupo e as ações rápidas.",
      "Utilize a busca, filtros por grupo e setor para localizar o colaborador desejado.",
      "Clique em “Novo usuário” para informar dados pessoais, grupo padrão, setor obrigatório e supervisores diretos; salve para liberar o acesso.",
      "Use as ações de cada linha para solicitar troca de senha, editar dados, remover usuários ou abrir o gerenciador de setores.",
    ],
    tips: [
      "Mantenha os supervisores diretos atualizados para que relatórios hierárquicos reflitam a estrutura real.",
      "Antes de remover um usuário, avalie as solicitações vinculadas para garantir que não haja pendências críticas.",
    ],
    related: ["Grupos", "Perfil", "Auditoria"],
  },
  {
    id: "mail-settings",
    title: "Configurações de e-mail",
    category: "Administração",
    audience: "TI",
    journeyStage: "Administração",
    summary:
      "Defina o servidor SMTP utilizado pelas notificações automáticas, atualize credenciais e envie e-mails de teste.",
    whenToUse: [
      "Configurar o servidor de saída de e-mails corporativos.",
      "Atualizar senha ou parâmetros após a rotação de credenciais.",
    ],
    steps: [
      "Acesse Configurações → E-mail para carregar o host, porta, remetente e usuário configurados atualmente.",
      "Atualize host, porta, nome do remetente, endereço de envio e usuário de autenticação; habilite TLS conforme exigência do provedor.",
      "Informe uma nova senha de autenticação quando necessário ou marque a opção de limpar senha para removê-la do armazenamento.",
      "Utilize o campo “Enviar e-mail de teste” para confirmar conectividade antes de salvar definitivamente as alterações.",
    ],
    tips: [
      "O formulário indica quando há senha cadastrada; limpe-a antes de trocar o usuário para evitar inconsistências.",
      "Guarde o endereço de teste utilizado para validar envios e identificar rapidamente problemas de entrega.",
    ],
    related: ["Notificações", "Automação", "Usuários"],
  },
  {
    id: "dashboard-analytics",
    title: "Painel analítico do dashboard",
    category: "Início",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Monitore tarefas, lembretes da agenda e indicadores avançados sem sair da tela inicial do dashboard.",
    whenToUse: [
      "Iniciar o dia avaliando solicitações críticas e a carga atual da equipe.",
      "Abrir rapidamente Agenda, Process Runner ou outras abas a partir dos atalhos exibidos no painel.",
    ],
    steps: [
      "Acesse Dashboard e utilize os botões “Visão operacional” e “Indicadores avançados” para alternar entre os painéis disponíveis.",
      "Revise os cartões de tarefas (Tarefas, No prazo, Em alerta, Atrasado) e clique em um deles para filtrar a fila prioritária.",
      "Use o bloco “Ações recomendadas” para abrir solicitações críticas com o botão “Abrir solicitação” ou encaminhá-las ao Process Runner.",
      "Acompanhe os lembretes vindos da Agenda no painel lateral e clique em “Abrir” quando precisar tratar um compromisso imediatamente.",
      "Na aba de indicadores avançados, ajuste o campo “Dias” para atualizar gráficos como “Últimos movimentos” e “Novas solicitações (Xd)”.",
    ],
    tips: [
      "Filtros aplicados aos cartões de tarefas permanecem ativos até que você volte para “Todas as tarefas”.",
      "Utilize o botão “Atualizar” presente no cabeçalho para recarregar contadores sempre que houver novas solicitações durante a sessão.",
    ],
    related: ["Agenda", "Solicitações", "Process Runner"],
  },
  {
    id: "finance-overview",
    title: "Painel financeiro e resumo das contas",
    category: "Financeiro",
    audience: "Financeiro",
    journeyStage: "Execução diária",
    summary:
      "Acompanhe saldos, movimentações e indicadores de conciliação por conta bancária em um único painel.",
    whenToUse: [
      "Verificar rapidamente créditos, débitos e divergências antes do fechamento diário.",
      "Preparar relatórios financeiros com base no extrato mais recente ou validar importações automáticas.",
    ],
    steps: [
      "Abra Financeiro para carregar a lista de contas bancárias cadastradas e os indicadores globais.",
      "Selecione uma conta para visualizar totais de créditos, débitos, saldo e contadores de conciliação.",
      "Utilize os botões “Resumo por categoria”, “Exportar relatório” e “Importar OFX” conforme a necessidade da análise.",
      "Clique em “Lançar transação manual” para registrar ajustes e em “Sincronizar conta” quando a integração estiver configurada como API.",
      "Acesse “Abrir gerenciador de categorias” para revisar o plano de contas antes de novos lançamentos ou conciliações.",
    ],
    tips: [
      "Os cartões de status (Conciliadas, Pendentes, Divergentes) ajudam a priorizar quais lançamentos revisar primeiro.",
      "Importe ou sincronize os extratos antes de exportar relatórios para garantir que os valores reflitam o cenário atual.",
    ],
    related: ["Conciliação bancária", "Transações manuais", "Relatórios"],
  },
  {
    id: "finance-accounts",
    title: "Cadastro de contas bancárias e integrações",
    category: "Financeiro",
    audience: "Financeiro",
    journeyStage: "Administração",
    summary:
      "Cadastre contas, defina o tipo de integração e mantenha descrições e credenciais alinhadas ao fluxo financeiro.",
    whenToUse: [
      "Habilitar uma nova conta para importação de extratos ou sincronização automática.",
      "Atualizar dados bancários, descrição interna ou remover contas que não serão mais conciliadas.",
    ],
    steps: [
      "No módulo Financeiro, clique em “Nova conta bancária” para abrir o formulário de cadastro.",
      "Informe nome interno, banco, agência, conta e uma descrição que ajude a equipe a identificar o uso daquela conta.",
      "Selecione o tipo de integração (Lançamentos manuais, Importação via OFX ou Integração bancária automática) e preencha o campo de configuração JSON quando necessário.",
      "Salve para disponibilizar a conta; utilize as ações “Editar conta bancária” ou “Excluir” sempre que precisar ajustar dados já cadastrados.",
      "Após ativar uma integração do tipo API, utilize “Sincronizar conta” para validar credenciais e carregar os primeiros lançamentos.",
    ],
    tips: [
      "Descreva no campo de observações como a conta é utilizada; isso facilita a identificação durante auditorias.",
      "Rotacione chaves de integração diretamente no JSON e aproveite o botão de sincronização para confirmar que novas credenciais estão válidas.",
    ],
    related: ["Painel financeiro e resumo das contas", "Conciliação bancária", "Transações manuais"],
  },
  {
    id: "finance-reconciliation",
    title: "Conciliação bancária",
    category: "Financeiro",
    audience: "Financeiro",
    journeyStage: "Execução diária",
    summary:
      "Filtre lançamentos, marque divergências e conclua conciliações com histórico documentado.",
    whenToUse: [
      "Fechar o dia conciliando extratos importados com lançamentos internos.",
      "Registrar observações sobre diferenças encontradas e sinalizar itens pendentes para revisão.",
    ],
    steps: [
      "Selecione a conta desejada e aplique filtros de status, período ou texto para reduzir a lista de transações.",
      "Marque manualmente as linhas ou utilize “Selecionar exibidas” para preparar o conjunto que será conciliado.",
      "Ative a opção “Marcar não selecionadas como divergentes” quando precisar sinalizar lançamentos que não batem com o extrato.",
      "Preencha “Observações da conciliação” com detalhes relevantes e finalize clicando em “Concluir conciliação”.",
      "Use as ações de cada transação para editar descrições, reclassificar categorias ou excluir lançamentos manuais quando necessário.",
    ],
    tips: [
      "O resumo de seleção apresenta totais de créditos, débitos e quantidade para comparar rapidamente com o extrato bancário.",
      "Notas registradas durante a conciliação aparecem na auditoria e ajudam a rastrear ajustes futuros.",
    ],
    related: ["Painel financeiro e resumo das contas", "Transações manuais", "Auditoria"],
  },
  {
    id: "finance-manual-transactions",
    title: "Transações manuais e categorias",
    category: "Financeiro",
    audience: "Financeiro",
    journeyStage: "Execução diária",
    summary:
      "Registre lançamentos pontuais, classifique receitas e despesas e mantenha o plano de contas alinhado ao relatório financeiro.",
    whenToUse: [
      "Incluir ajustes que não vieram do extrato ou registrar despesas extraordinárias.",
      "Preparar categorias antes de gerar o resumo por centro de custo.",
    ],
    steps: [
      "Com uma conta selecionada, clique em “Lançar transação manual” para abrir o formulário de lançamento.",
      "Defina se o movimento é Crédito ou Débito, informe valor, data de ocorrência e descrição detalhada.",
      "Associe categorias e subcategorias utilizando os seletores disponíveis; o formulário apresenta apenas subcategorias da categoria escolhida.",
      "Utilize o campo “Observações” para contextualizar o lançamento e clique em “Salvar transação” para aplicá-lo imediatamente.",
      "Sempre que precisar criar novas categorias ou revisar cores e limites, utilize o botão “Abrir gerenciador de categorias”.",
    ],
    tips: [
      "Mensagens de validação aparecem acima do formulário quando houver campos obrigatórios em branco ou valores inválidos.",
      "Categorias e subcategorias definem os gráficos exibidos em “Resumo por categoria”; mantenha-as organizadas para relatórios mais ricos.",
    ],
    related: ["Conciliação bancária", "Painel financeiro e resumo das contas"],
  },
  {
    id: "asset-management-overview",
    title: "Gestão do patrimônio corporativo",
    category: "Patrimônio",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Controle ativos por categoria e localização, acompanhe depreciação e visualize manutenções pendentes em tempo real.",
    whenToUse: [
      "Mapear o parque de ativos físicos antes de auditorias ou inventários.",
      "Identificar equipamentos com manutenção próxima ou valor contábil reduzido.",
    ],
    steps: [
      "Abra Patrimônio para consultar os cartões de indicadores e aplicar filtros por status, categoria ou localização.",
      "Utilize a tabela principal para conferir tag, série, custódia e valores de cada ativo.",
      "Acesse as ações “Manutenção”, “Editar” e “Excluir” para atualizar dados ou abrir intervenções específicas.",
      "Revise o quadro “Histórico recente” para acompanhar intervenções registradas e identificar responsáveis.",
      "Exporte a visão atual com o botão “Exportar CSV” sempre que precisar compartilhar o inventário completo.",
    ],
    tips: [
      "Os selos de status (Em operação, Em manutenção, Em estoque, Baixado) ajudam a priorizar inspeções.",
      "Use filtros combinados de categoria e localização para localizar rapidamente ativos críticos durante auditorias.",
    ],
    related: ["Manutenções de ativos", "Inventário", "Relatórios"],
  },
  {
    id: "asset-maintenance",
    title: "Manutenções de ativos",
    category: "Patrimônio",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Registre manutenções preventivas, corretivas ou calibragens e acompanhe o ciclo até a conclusão.",
    whenToUse: [
      "Programar intervenções obrigatórias ou registrar correções emergenciais.",
      "Atualizar status e custos após a execução do serviço de manutenção.",
    ],
    steps: [
      "Selecione um ativo e clique em “Manutenção” para abrir o formulário dedicado.",
      "Escolha o tipo (Preventiva, Corretiva, Preditiva ou Calibração), defina status e informe datas planejadas ou realizadas.",
      "Registre responsável, custo estimado e notas detalhando a intervenção.",
      "Salve para adicionar o registro e utilize o botão “Concluir” no histórico quando a manutenção for finalizada.",
      "Exclua ou reprograme intervenções diretamente pelo histórico caso haja mudanças de escopo.",
    ],
    tips: [
      "Manutenções abertas aparecem no resumo do ativo, facilitando a priorização de equipes técnicas.",
      "Registre custos sempre que possível para alimentar indicadores de depreciação e planejamento orçamentário.",
    ],
    related: ["Gestão do patrimônio corporativo", "Inventário", "Agenda"],
  },
  {
    id: "inventory-overview",
    title: "Controle de estoque e produtos",
    category: "Estoques",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Cadastre itens, monitore níveis de estoque e aplique filtros dinâmicos por depósito, categoria e status.",
    whenToUse: [
      "Acompanhar itens críticos, rupturas ou excedentes antes de planejar compras.",
      "Exportar relatórios atualizados para fornecedores ou áreas de controladoria.",
    ],
    steps: [
      "Acesse Controle de Estoque para visualizar indicadores iniciais como itens cadastrados, valor total e saídas do período.",
      "Utilize a pesquisa e os filtros por depósito, categoria, status e formato de relatório para refinar a visualização.",
      "Clique em “Novo item” para cadastrar produtos com SKU, unidades, limites mínimos/máximos e custos.",
      "Acompanhe a tabela principal para verificar validade, última movimentação e ações disponíveis (Movimentar, Editar, Excluir).",
      "Gere relatórios em PDF ou Excel com o botão “Exportar” após escolher o formato desejado.",
    ],
    tips: [
      "O destaque em amarelo sinaliza itens abaixo do mínimo definido; ajuste o filtro de status para focar nesses casos.",
      "Utilize o botão “Atualizar” sempre que novos lançamentos forem registrados por integrações externas.",
    ],
    related: ["Movimentações de estoque", "Inventário", "Relatórios"],
  },
  {
    id: "inventory-movements",
    title: "Movimentações de estoque",
    category: "Estoques",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Registre entradas, saídas e transferências com rastreabilidade por depósito e referência operacional.",
    whenToUse: [
      "Atualizar estoque após recebimento, faturamento ou transferência entre unidades.",
      "Investigar movimentações recentes e validar responsáveis ou códigos de referência.",
    ],
    steps: [
      "Localize o item desejado e clique em “Movimentar” para abrir o formulário de registro.",
      "Escolha o tipo (Entrada, Saída ou Transferência), informe quantidades, depósitos de origem/destino e referência.",
      "Defina o custo unitário quando necessário e ajuste a data/hora em “Ocorreu em” para refletir o momento real do movimento.",
      "Salve para atualizar imediatamente os saldos e consulte a seção “Movimentações recentes” para validar o registro.",
      "Use os filtros por tipo, depósito e período para localizar operações específicas durante auditorias.",
    ],
    tips: [
      "As quantidades exibidas em verde representam entradas; valores em vermelho identificam saídas e facilitam a análise visual.",
      "Preencha o código de referência para conectar o movimento ao pedido, NF ou solicitação correspondente.",
    ],
    related: ["Controle de estoque e produtos", "Solicitações", "Auditoria"],
  },
  {
    id: "registration-center-overview",
    title: "Central de Cadastros",
    category: "Cadastros",
    audience: "Administradores",
    journeyStage: "Execução diária",
    summary:
      "Pesquise registros de processos publicados, edite dados coletados e controle campos extras diretamente em uma única tela.",
    whenToUse: [
      "Consultar formulários enviados sem precisar abrir a solicitação completa.",
      "Atualizar informações após o encerramento do processo mantendo trilha de auditoria.",
    ],
    steps: [
      "Acesse a Central de Cadastros e selecione o processo desejado para carregar o quadro de consultas.",
      "Utilize filtros por status, período e busca textual para encontrar rapidamente a solicitação desejada.",
      "Clique em uma linha para exibir os detalhes; use “Gerenciar campos extras” quando precisar adicionar metadados complementares.",
      "Edite valores diretamente nos grupos exibidos e salve para registrar as alterações permitidas.",
      "Recarregue os dados pelo botão “Recarregar” sempre que precisar confirmar mudanças aplicadas por outros usuários.",
    ],
    tips: [
      "Solicitações em andamento podem ter campos bloqueados; finalize o processo ou utilize páginas dedicadas para liberar a edição.",
      "Use o painel de resumo no topo para validar solicitante, status e data de início antes de alterar qualquer informação.",
    ],
    related: ["Personalização de relatórios", "Formulários", "Solicitações"],
  },
  {
    id: "registration-center-exports",
    title: "Personalização de relatórios na Central",
    category: "Cadastros",
    audience: "Administradores",
    journeyStage: "Administração",
    summary:
      "Configure colunas, gere relatórios em diferentes formatos e organize páginas dedicadas para cada processo.",
    whenToUse: [
      "Montar planilhas específicas para auditoria, compliance ou integrações pontuais.",
      "Criar páginas dedicadas para equipes que precisam acessar um subconjunto de cadastros.",
    ],
    steps: [
      "Abra o menu de opções e selecione “Personalizar colunas” para escolher quais campos aparecem no quadro de consultas.",
      "Utilize “Gerenciar campos extras” para adicionar dados derivados (IDs, status, solicitante, vínculos).",
      "Clique em “Exportar XLS” ou “Exportar XLSX” para gerar relatórios utilizando os filtros atualmente ativos.",
      "Use “Adicionar Página” para criar uma visão dedicada com nome, ícone e filtros pré-configurados.",
      "Remova páginas obsoletas pelo mesmo painel mantendo o acesso aos registros na visão principal.",
    ],
    tips: [
      "A tarja inferior do modal de exportação apresenta quantos registros serão incluídos conforme os filtros aplicados.",
      "Ao criar páginas dedicadas, utilize ícones e cores padronizados para facilitar a navegação dos usuários finais.",
    ],
    related: ["Central de Cadastros", "Formulários", "Relatórios"],
  },
  {
    id: "human-resources-overview",
    title: "Visão geral de Recursos Humanos",
    category: "Pessoas",
    audience: "Gestores",
    journeyStage: "Execução diária",
    summary:
      "Acompanhe indicadores de headcount, férias, aniversários e acesso ao sistema no painel de Recursos Humanos.",
    whenToUse: [
      "Conferir rapidamente o quadro de colaboradores por departamento, vínculo e status.",
      "Preparar reuniões de acompanhamento de pessoas com dados consolidados em um único lugar.",
    ],
    steps: [
      "Abra Recursos Humanos para carregar os cartões de resumo com totais de colaboradores ativos, férias, licenças e acessos.",
      "Ajuste os filtros de busca, status, departamento ou tipo de vínculo para refinar a tabela principal e os gráficos.",
      "Analise os painéis de distribuição por departamento, vínculo e as listas de admissões recentes, aniversariantes e colaboradores sem acesso.",
      "Consulte a seção “Férias ou licença” para visualizar delegados definidos, datas de retorno e observações cadastradas.",
    ],
    tips: [
      "Utilize o botão “Atualizar” sempre que concluir cadastros ou importações para sincronizar indicadores e listagens.",
      "Observe os selos de status coloridos na tabela para identificar rapidamente quem está em férias, licença ou desligamento.",
    ],
    related: ["Minha equipe", "Organograma", "Gestão de usuários"],
  },
  {
    id: "human-resources-records",
    title: "Cadastro completo de colaboradores",
    category: "Pessoas",
    audience: "Administradores",
    journeyStage: "Administração",
    summary:
      "Cadastre, edite e gerencie colaboradores incluindo dados pessoais, contratuais, endereços e acesso ao sistema.",
    whenToUse: [
      "Realizar onboarding de novos colaboradores ou atualizar cadastros existentes.",
      "Registrar férias, licenças, delegados e vínculos com usuários da plataforma.",
    ],
    steps: [
      "Clique em “Cadastrar colaborador” para abrir o formulário completo com dados pessoais, contratuais e endereço profissional.",
      "Associe o cadastro a um usuário do sistema quando precisar conceder acesso; os campos principais são preenchidos automaticamente conforme o usuário selecionado.",
      "Defina status (Ativo, Férias, Licença, Inativo ou Desligado), configure delegados e informe datas de admissão ou término conforme necessário.",
      "Salve para criar ou atualizar o registro e utilize as ações da tabela para editar novamente ou excluir colaboradores obsoletos.",
      "Anote observações relevantes no campo de notas para manter o histórico centralizado no próprio cadastro.",
    ],
    tips: [
      "Somente usuários com permissão de gestão de RH visualizam o botão de cadastro e as ações de edição ou exclusão.",
      "Preencha matrícula e tipo de vínculo para facilitar integrações com folha de pagamento ou relatórios financeiros.",
    ],
    related: ["Visão geral de Recursos Humanos", "Gestão de usuários", "Agenda colaborativa"],
  },
  {
    id: "human-resources-reports",
    title: "Relatórios e exportações de RH",
    category: "Pessoas",
    audience: "Gestores",
    journeyStage: "Administração",
    summary:
      "Gere relatórios em CSV ou PDF com filtros personalizados e utilize modelos prontos no painel de relatórios avançados.",
    whenToUse: [
      "Compartilhar dados com contabilidade, folha ou comitês de pessoas.",
      "Registrar evidências para auditorias trabalhistas ou revisões internas de compliance.",
    ],
    steps: [
      "Aplique filtros de status, departamento, vínculo ou busca livre para preparar o recorte desejado na lista de colaboradores.",
      "Utilize os botões “Exportar CSV” ou “Exportar PDF” para gerar arquivos com os dados filtrados da visão atual.",
      "Abra “Relatórios avançados” para acessar modelos prontos, como colaboradores sem acesso, por status ou por departamento.",
      "No modal, escolha o relatório e confirme o download; mensagens de erro aparecem no próprio painel quando houver inconsistências.",
      "Armazene os arquivos gerados conforme a política interna e registre o envio em seu controle de compliance, quando aplicável.",
    ],
    tips: [
      "Durante a geração de arquivos, aguarde o término indicado no botão antes de solicitar um novo download.",
      "Combine filtros por status e departamento para gerar planilhas específicas, como férias programadas por equipe.",
    ],
    related: ["Visão geral de Recursos Humanos", "Personalização de relatórios na Central", "Auditoria do sistema"],
  },
  {
    id: "assistant-ai",
    title: "Assistente IA do Studio",
    category: "Inteligência",
    audience: "Gestores",
    journeyStage: "Início rápido",
    summary:
      "Cocrie processos completos respondendo às perguntas do assistente e receba formulários, fluxos BPMN e recomendações automáticas.",
    whenToUse: [
      "Iniciar a modelagem de um novo processo com apoio guiado.",
      "Gerar rapidamente formulário, regras e fluxo para validar uma ideia com o time.",
    ],
    steps: [
      "Acesse o módulo Assistente IA do Studio para visualizar o painel com progresso e etapas da jornada.",
      "Responda às perguntas exibidas na seção “Conversa com o assistente”; utilize sugestões rápidas quando estiver sem inspiração.",
      "Acompanhe o indicador de progresso para saber quantas etapas faltam e ajuste respostas antes de avançar.",
      "Ao finalizar, revise o resumo gerado, os componentes de formulário e o diagrama BPMN sugerido.",
      "Use o botão “Iniciar nova orientação” para reiniciar o fluxo e criar outra versão ou processo.",
    ],
    tips: [
      "Você pode pausar a conversa; o assistente mantém o progresso e os rascunhos gerados até que a orientação seja concluída.",
      "Aplique os artefatos gerados diretamente nos módulos Formulários e Processos para acelerar a publicação.",
    ],
    related: ["Modelagem de processos", "Construtor de formulários", "Painel analítico do dashboard"],
  },
];

